export const ordermanagementhelp = {
  BulkOrderUpload: {
    title: "Bulk Order Upload",
    description:
      "The Bulk Order Upload feature enables users to efficiently submit multiple orders at once using a standardized Excel template. By automating the order entry process, it minimizes manual effort, ensures data accuracy through built-in validation, and accelerates order processing with automatic creation and approval. This feature is designed to save time, reduce errors, and streamline operations, making bulk order management faster and more reliable.",

    steps: [
      {
        title: "Step 1: Download the Template",
        details:
          "To ensure your file has the correct format and all required fields, you must use the official template.\n\n• Click the *Template* button.\n• The *Template_OrderBulkUpload.xlsx* file will download to your computer.\n• Open the file and fill in your order details. Make sure you enter data in the correct format for each column (e.g., date, number, text).",
      },
      {
        title: "Step 2: Upload and Validate the File",
        details:
          "Once your template is filled out, upload it to the system for validation.\n\n• Click the *Select Excel File to Upload* button. A file explorer window will open.\n• Navigate to and select your completed *Template_OrderBulkUpload.xlsx* file. The file name will appear next to the button.\n• Click the *Validate* button. The system will process your file and display the results in the grid below.\n• The *Status* column for each order will show *Not Validated* while processing, then update to *Validated* or *Validation Failed* once complete.",
      },
      {
        title: "Step 3: Review Validation Results and Fix Errors",
        details:
          "After validation, review the results to identify and correct any errors.\n\n• If validation is successful: The *Status* column for all orders will show *Validated*. The *Save* button will become enabled.\n• If validation fails: The *Status* column for affected orders will show *Validation Failed*. The *Validation Remarks* column will provide specific reasons for each failure (e.g., 'Mandatory field PO No is missing' or 'Incorrect date format').\n• Return to your Excel file, correct the errors based on the remarks, and then re-upload and re-validate the updated file. Orders cannot be saved until all validation errors are resolved.",
      },
      {
        title: "Step 4: Save the Orders",
        details:
          "Once all orders have successfully passed validation, you can save them to the system.\n\n• Ensure all orders in the grid show *Validated* in the *Status* column.\n• The *Save* button will now be active. Click it.\n• A confirmation message will appear. Confirm the save action.\n• The system will process and automatically approve the orders. You will receive a notification upon successful completion.",
      },
    ],

    fields: [
      { field: "Customer Code", required: true, hint: "Unique identifier for the customer placing the order." },
      { field: "PO No", required: true, hint: "Purchase Order number provided by the customer. Must be unique." },
      { field: "Order Date", required: true, hint: "Date of the order in the specified format (e.g., DD-MMM-YYYY)." },
      { field: "Item Code", required: true, hint: "Unique product code as defined in the item master." },
      { field: "Quantity", required: true, hint: "Number of units ordered. Must be numeric." },
      { field: "Delivery Date", required: false, hint: "Expected date of delivery. Optional, but recommended for scheduling." },
    ],

    buttons: [
      { name: "Template", hint: "Downloads the official Excel template for bulk order upload." },
      { name: "Select Excel File to Upload", hint: "Opens a file explorer to choose your completed Excel file for upload." },
      { name: "Validate", hint: "Validates the uploaded file and displays the validation results in the grid." },
      { name: "Save", hint: "Saves validated orders to the system and triggers automatic approval." },
    ],

    statusLegend: [
      { status: "🟡 Not Validated", meaning: "The order file has been uploaded but not yet checked for errors." },
      { status: "🟢 Validated", meaning: "The order has passed all validation checks and is ready to be saved." },
      { status: "🔴 Validation Failed", meaning: "The order contains errors that must be corrected before saving." },
    ],

    bestPractices: [
      "Always use the latest version of the Excel template.",
      "Ensure all mandatory fields are filled correctly.",
      "Follow proper formats for dates, numbers, and text.",
      "Validate the file before saving to avoid errors.",
      "Keep a copy of the uploaded file for reference.",
    ],

    examples: [
      {
        CustomerCode: "CUST001",
        PONo: "PO-2025-001",
        OrderDate: "23-Sep-2025",
        ItemCode: "ITEM-1001",
        Quantity: "50",
        Status: "Validated",
      },
      {
        CustomerCode: "CUST002",
        PONo: "PO-2025-002",
        OrderDate: "24-Sep-2025",
        ItemCode: "ITEM-2002",
        Quantity: "20",
        Status: "Validation Failed",
      },
    ],

    
  },

  OrderManagementSystem: {
     title: "Order Management System",
    description:
      "The Order Management System (OMS) streamlines the process of creating, managing, and tracking sales orders. It supports order creation, editing, amendments with audit trail, advanced filtering, logistics configuration, pricing, taxes, and print-ready reports.",

    overviewPoints: [
      "Order Creation with full customer, pricing, and product configuration.",
      "Order Listing with advanced filters.",
      "Editing & Amendments with mandatory audit reason.",
      "Pricing, Discount, Tax & Logistics Automation.",
      "Print-ready formatted reports."
    ],

    steps: [
      {
        title: "Order Listing Screen",
        purpose:
          "Provides a complete view of approved, pending, and cancelled orders with advanced filtering and navigation to order details.",
        keyFeatures: [
          {
            name: "Date Range Selector",
            details:
              "Allows filtering of orders by Start Date and End Date. Clicking Apply will refresh the list."
          },
          {
            name: "Column Filters",
            details:
              "Each column has a filter icon that helps search by Customer, City, Status, etc."
          },
          {
            name: "Clickable Order Numbers (CO. NO.)",
            details:
              "Each CO. NO. is a hyperlink that opens the full Order Entry screen."
          }
        ],
        usageTips: [
          "Combine Date Range + Column Filters for precise search.",
          "Use CO. NO. links to directly open order details."
        ],
        columns: [
          "Co. No.",
          "Co. Date",
          "Status",
          "Bill To Party",
          "Bill To City",
          "Bill To State",
          "Ship To Party",
          "Ship To City",
          "Ship To State"
        ]
      },

      {
        title: "Create & Approve New Order",
        steps: [
          "Click '+ New' from Order Listing.",
          "Order Entry screen opens.",
          "Enter all mandatory details (*).",
          "Add Line Items using 'Add Product'.",
          "Click Save to store as Draft.",
          "Click Approve to finalize and enable processing."
        ]
      },

      {
        title: "View or Edit Existing Order",
        steps: [
          "Open the desired order using CO. NO.",
          "Click Amend to enable modifications.",
          "Enter mandatory Amendment Reason.",
          "Update fields as required.",
          "Click Save & Approve to confirm changes."
        ],
        tips: [
          "Provide clear amendment reasons for audit.",
          "Only approved orders can be amended.",
          "Double-check line items before saving."
        ]
      }
    ],

    fields: {
      headerSection: [
        { field: "CO. NO.", description: "Auto-generated unique number." },
        { field: "Order Date", description: "Date of order creation." },
        { field: "Bill To Customer", description: "Customer being billed." },
        { field: "Ship To Customer", description: "Recipient of goods." },
        { field: "Shipment Mode", description: "Road / Rail / Air / Local." },
        { field: "Transporter", description: "Transport vendor." },
        { field: "Price List", description: "Pricing mechanism for calculation." },
        { field: "Payment Term", description: "Immediate, Net 30, etc." },
        { field: "PO No / Date", description: "Customer reference details." },
        { field: "Schedule Date", description: "Expected delivery date." }
      ],

      lineItemSection: [
        { field: "Variety", description: "Product variety." },
        { field: "GSM", description: "Weight specification." },
        { field: "Width/Length", description: "Dimensions." },
        { field: "Unit", description: "Measurement unit." },
        { field: "Color", description: "Product color." },
        { field: "Grain", description: "S/P alignment." },
        { field: "Package Mode", description: "Reel / Bundle / Pallet." },
        { field: "Req. Qty (Kg)", description: "Requested weight." },
        { field: "Conf. Qty (Kg)", description: "Confirmed weight." }
      ],

      addProductFields: [
        { field: "Group*", hint: "Mandatory product group." },
        { field: "Sub Group*", hint: "Product category." },
        { field: "Variety*", hint: "Product name." },
        { field: "Grade*", hint: "Primary / Secondary." },
        { field: "HSN Code*", hint: "Tax classification." },
        { field: "GSM*", hint: "Mandatory weight spec." },
        { field: "BF", hint: "Burst Factor (optional)." },
        { field: "Size", hint: "Width × Length." },
        { field: "Reel OD", hint: "Outer diameter of reel." },
        { field: "Core OD", hint: "Core diameter." },
        { field: "Order Qty (Kg)*", hint: "Required order weight." },
        { field: "CO Qty (Kg)*", hint: "Confirmed weight." }
      ]
    },

    functionalRules: [
      "If Length = 0 → Sheets disabled.",
      "If Length > 0 → Reels disabled.",
      "Entering Order Qty auto-calculates Reels.",
      "Entering No. of Reels auto-calculates Order Qty.",
      "Sheet Orders auto-populate default sheet counts.",
      "Save adds product to Line Items."
    ],

    summarySection: [
      "Total Qty (Kg)",
      "Gross Amount",
      "Discount Amount",
      "Taxable Amount",
      "Tax Amount",
      "Insurance",
      "Service Charge",
      "Total Amount"
    ],

    remarks: [
      "Remarks – optional instructions.",
      "Amendment Reason – mandatory for edits."
    ],

    buttons: [
      { name: "New", hint: "Create a new order." },
      { name: "Amend", hint: "Enable editing of approved order." },
      { name: "Save", hint: "Save draft or amendments." },
      { name: "Save & Approve", hint: "Finalize and approve order." },
      { name: "Cancel", hint: "Close without saving." },
      { name: "Print", hint: "Generate print-ready report." }
    ],

    bestPractices: [
      "Enter correct Schedule Date.",
      "Verify Credit Limit before saving.",
      "Double-check quantities.",
      "Use filters to find orders quickly."
    ]
      
  },

  PriceListManagementSystem: {
    title: "Price List Management System (PLMS)",
    description:
      "The Price List Management System (PLMS) streamlines the process of creating, maintaining, and updating sales price lists. It offers a user-friendly interface with GSM-based pricing flexibility and state/dealer-level applicability. PLMS simplifies pricing management through base price referencing, effective date control, and organized breakdowns for reel and sheet pricing.",

    features: [
      "Price List Creation & Maintenance – Create, edit, and manage price lists with effective start and end dates.",
      "Base Price List Reference – Copy from existing price lists and adjust prices by adding or deducting values.",
      "Applicable States & Dealers – Assign price lists to specific states, zones, or customer groups.",
      "GSM Range Breakdown – Define pricing based on GSM ranges for each product quality with reel and sheet pricing.",
      "Version Control & Status – Manage active and inactive lists with proper version and validity tracking.",
    ],

    screens: [
      {
        name: "Price List Index Screen",
        purpose:
          "The Price List Index Screen serves as the central dashboard for managing all price lists. Users can create, view, and edit price lists easily from this interface.",
        steps: [
          { title: "List View", details: "Displays all available price lists with key information such as code, name, status, and validity period." },
          { title: "+ New Button", details: "Click the + New button to create a new price list entry." },
          { title: "Row Click for Editing", details: "Click any existing price list row to open it in the Price List Entry Screen for editing or review." },
          { title: "Search & Filters", details: "Use search and filter options to find price lists by code, name, or status." },
        ],
      },

      // (other PLMS screens...)

      {
        name: "HSN Master",
        purpose: "Manage Harmonized System of Nomenclature (HSN) codes and related GST rates.",
      },
    ],

    buttons: [
      { name: "✅ Save", hint: "Save new or modified price list data." },
      { name: "❌ Cancel", hint: "Discard unsaved changes and return to the previous screen." },
      { name: "➕ Add New GSM Range Row", hint: "Insert a new GSM entry row in the breakdown table." },
      { name: "🗑 Delete", hint: "Remove the selected GSM range row." },
      { name: "🔍 Show Breakdown Details", hint: "Display existing GSM range breakdowns for the selected list." },
    ],

    bestPractices: [
      "Always define both Effective Start and End Dates to avoid overlapping price lists.",
      "Use Base Price List with Add/Deduct adjustments for quick creation of new versions.",
      "Regularly review Applicable States and Dealers to ensure accurate price targeting.",
      "Maintain logical GSM ranges without overlaps or gaps.",
      "Verify all pricing entries before saving to prevent incorrect values in transactions.",
    ],

    
HSNMaster: {
    title: "HSN Master",
    description:
      "The HSN Master module enables management of Harmonized System of Nomenclature (HSN) codes and their associated GST rates (CGST, SGST, IGST). It ensures correct tax calculation for sales and purchase transactions and allows easy addition, editing, and deletion of HSN codes.",

    features: [
      "HSN Code Listing – View all HSN codes entered in the system.",
      "New HSN Entry – Quickly add new HSN codes with tax details.",
      "Edit HSN Code – Update description or tax rates (CGST, SGST, IGST).",
      "Delete HSN Code – Remove unused HSN codes safely.",
      "Search & Filter – Locate HSN codes quickly by code or description."
    ],

    steps: [
      {
        name: "HSN Master Index Screen",
        purpose:
          "The HSN Master Index screen lists all HSN codes and their tax details. Users can add new codes, edit existing ones, or delete unused codes.",
        steps: [
          { title: "List View", details: "Displays all HSN codes with Description, CGST %, SGST %, IGST %." },
          { title: "+ New Button", details: "Click to add a new HSN entry row." },
          { title: "Row Click for Editing", details: "Click on the HSN Code link to edit its details." },
          { title: "Search & Filters", details: "Search by HSN Code or Description." }
        ],
        columnsDisplayed: [
          { column: "HSN Code", description: "Unique code representing a product category. Clickable to edit." },
          { column: "Description", description: "Short description of the HSN code." },
          { column: "CGST %", description: "Central GST percentage applicable to this code." },
          { column: "SGST %", description: "State GST percentage applicable." },
          { column: "IGST %", description: "Integrated GST percentage applicable." }
        ]
      },

      {
        name: "New HSN Code Entry",
        purpose: "To add a new HSN code with its tax details.",
        steps: [
          { title: "Step 1", details: "Click + New on the HSN Master Index screen." },
          { title: "Step 2", details: "Fill in all mandatory fields (*) in the entry form." },
          { title: "Step 3", details: "Enter CGST, SGST, and IGST percentages as per applicable tax rates." },
          { title: "Step 4", details: "Click Save to store the new HSN code." }
        ],
        fieldDetails: [
          { field: "HSN Code*", description: "Unique numeric or alphanumeric code." },
          { field: "Description", description: "Optional short description of the product category." },
          { field: "CGST %*", description: "Central GST rate for this HSN code." },
          { field: "SGST %*", description: "State GST rate for this HSN code." },
          { field: "IGST %*", description: "Integrated GST rate for interstate transactions." }
        ]
      },

      {
        name: "Editing an HSN Code",
        purpose: "Update details for an existing HSN code.",
        steps: [
          { title: "Step 1", details: "Click on the HSN Code link in the list." },
          { title: "Step 2", details: "Update the Description or tax percentages (CGST, SGST, IGST)." },
          { title: "Step 3", details: "Click Save to store the changes." }
        ],
        notes: [
          "Fields may be locked if the HSN code is already linked to transactions.",
          "Verify tax percentages before saving to prevent incorrect GST calculation."
        ]
      },

      {
        name: "Deleting an HSN Code",
        purpose: "Remove HSN codes that are not linked to any transactions.",
        steps: [
          { title: "Step 1", details: "Select the HSN code row to be deleted." },
          { title: "Step 2", details: "Click Delete." },
          { title: "Step 3", details: "Confirm deletion in the pop-up dialog." }
        ],
        notes: [
          "HSN codes used in transactions cannot be deleted.",
          "Only delete unused or incorrectly entered codes."
        ]
      }
    ],

    buttons: [
      { name: "✅ Save", hint: "Save new or edited HSN code details." },
      { name: "❌ Cancel", hint: "Discard changes to current row." },
      { name: "➕ New", hint: "Insert a new HSN entry row." },
      { name: "🗑 Delete", hint: "Remove selected HSN code (if not linked to transactions)." },
      { name: "🔍 Search", hint: "Find HSN codes by code or description." }
    ],

    bestPractices: [
      "Ensure all HSN codes are unique.",
      "Verify tax rates according to current GST regulations.",
      "Regularly review HSN codes for updates or corrections.",
      "Avoid deleting HSN codes linked to existing transactions.",
      "Maintain accurate descriptions for easier identification."
    ],
}


  },
};
